import { ClientTable } from "../../../components/ui/ClientTable";
import { TablePagination } from "../../../components/ui/TablePagination";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Package } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import "../inventory.css";
import { fetchWarehouseById, persistLocationStockLevelChanges, fetchConfiguredStockLevelMap } from "../../../services/warehouseService";
import {
  searchProductsBySafetyStock,
  fetchCategoryDropdownOptions,
  toMaintenanceReserveFlag,
} from "../../../services/productCatalogService";

const TOAST_KEY = "warehouse_list_toast";
/** GET /product-catalog/search/safetystock?page=0&size=10&sortBy=productName&sortDir=ASC */
const PRODUCT_PAGE_SIZE = 10;

let productServiceNameMapPromise = null;

function getProductServiceNameMap() {
  if (!productServiceNameMapPromise) {
    productServiceNameMapPromise = (async () => {
      const { categories } = await fetchCategoryDropdownOptions({ size: 100 });
      const map = new Map();
      (categories || []).forEach((c) => {
        const name = c.name || c.label;
        if (!name) return;
        if (c.id != null && c.id !== "") map.set(String(c.id), name);
        if (c.productServiceId != null && c.productServiceId !== "") {
          map.set(String(c.productServiceId), name);
        }
      });
      return map;
    })().catch((err) => {
      productServiceNameMapPromise = null;
      throw err;
    });
  }
  return productServiceNameMapPromise;
}

/** Prefer productServiceId → FSM service name for the Category column. */
function withCategoryNames(products, serviceNameMap) {
  return (products || []).map((product) => {
    const serviceId = product.productServiceId;
    if (serviceId == null || serviceId === "") return product;
    const name = serviceNameMap.get(String(serviceId));
    return name ? { ...product, category: name } : product;
  });
}

async function loadSafetyStockProducts(page) {
  const result = await searchProductsBySafetyStock({
    page,
    size: PRODUCT_PAGE_SIZE,
    sortBy: "productName",
    sortDir: "ASC",
  });
  const batch = result.content || result.products || [];
  let content = batch;
  try {
    const serviceNameMap = await getProductServiceNameMap();
    content = withCategoryNames(batch, serviceNameMap);
  } catch {
    content = batch;
  }
  return {
    ...result,
    content,
    products: content,
  };
}

function isProductFlagEnabled(product, mappedKey, rawKeys) {
  const raw = product?.raw;
  if (raw && typeof raw === "object") {
    for (const key of rawKeys) {
      if (raw[key] !== undefined && raw[key] !== null && raw[key] !== "") {
        return toMaintenanceReserveFlag(raw[key]);
      }
    }
  }
  return toMaintenanceReserveFlag(product?.[mappedKey]);
}

/** Product checkbox "Service Safety Stock" (serviceSafetyStockFlag). */
function isServiceSafetyEnabled(product) {
  return isProductFlagEnabled(product, "serviceReserved", [
    "serviceSafetyStockFlag",
    "service_safety_stock_flag",
  ]);
}

/** Product checkbox "Sales Safety Stock" (salesSafetyStockFlag). */
function isSalesSafetyEnabled(product) {
  return isProductFlagEnabled(product, "safetyStock", [
    "salesSafetyStockFlag",
    "sales_safety_stock_flag",
  ]);
}

function StockQtyInput({ id, label, productName, value, canEdit, onChange }) {
  return (
    <input
      id={id}
      type="number"
      min="0"
      step="1"
      inputMode="numeric"
      className="form-input"
      placeholder="0"
      aria-label={`${productName} ${label}`}
      value={value}
      readOnly={!canEdit}
      disabled={!canEdit}
      onChange={(e) => canEdit && onChange(e.target.value)}
      style={{
        width: "120px",
        height: "38px",
        textAlign: "right",
        borderRadius: "8px",
        borderColor: "#cbd5e1",
        marginLeft: "auto",
        backgroundColor: canEdit ? undefined : "#f8fafc",
        cursor: canEdit ? undefined : "not-allowed",
        opacity: canEdit ? 1 : 0.85,
      }}
    />
  );
}

function ProductStockMobileCard({
  product,
  serviceValue,
  salesValue,
  onServiceChange,
  onSalesChange,
  serviceEditable,
  salesEditable,
}) {
  return (
    <div className="p-4 space-y-3">
      <div className="min-w-0 space-y-1">
        <h4 className="font-semibold text-foreground leading-snug">{product.name}</h4>
        <p className="text-xs text-[var(--color-text-secondary)] font-mono">
          {product.partNumber || i18n.t("inventory.emDash")}
        </p>
        <p className="text-xs text-[var(--color-text-secondary)]">
          {product.category || i18n.t("inventory.emDash")}
        </p>
      </div>
      <div className="flex justify-between items-center gap-3 pt-1">
        <label
          htmlFor={`sales-stock-mobile-${product.id}`}
          className="text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide shrink-0"
        >
          <Trans i18nKey="inventory.salesSafetyStockColumn" />
        </label>
        <StockQtyInput
          id={`sales-stock-mobile-${product.id}`}
          label={i18n.t("inventory.salesSafetyStockColumn")}
          productName={product.name}
          value={salesValue}
          canEdit={salesEditable}
          onChange={(value) => onSalesChange(product.id, value)}
        />
      </div>
      <div className="flex justify-between items-center gap-3">
        <label
          htmlFor={`service-stock-mobile-${product.id}`}
          className="text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide shrink-0"
        >
          <Trans i18nKey="inventory.minimumStockLevelColumn" />
        </label>
        <StockQtyInput
          id={`service-stock-mobile-${product.id}`}
          label={i18n.t("inventory.minimumStockLevelColumn")}
          productName={product.name}
          value={serviceValue}
          canEdit={serviceEditable}
          onChange={(value) => onServiceChange(product.id, value)}
        />
      </div>
    </div>
  );
}

export default function ConfigureWarehouseStockLevels() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState(null);
  const [products, setProducts] = useState([]);
  const [productById, setProductById] = useState({});
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [scopeId, setScopeId] = useState(id);
  const [reloadKey, setReloadKey] = useState(0);
  const [serviceLevels, setServiceLevels] = useState({});
  const [salesLevels, setSalesLevels] = useState({});
  const [initialServiceLevels, setInitialServiceLevels] = useState({});
  const [initialSalesLevels, setInitialSalesLevels] = useState({});
  const [configuredProductIds, setConfiguredProductIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const locationDetailsRef = useRef({ id: null, reloadKey: -1, warehouse: null, configured: null });

  if (scopeId !== id) {
    setScopeId(id);
    setPage(0);
    setWarehouse(null);
    setProducts([]);
    setProductById({});
    setTotalPages(0);
    setTotalElements(0);
    setServiceLevels({});
    setSalesLevels({});
    setInitialServiceLevels({});
    setInitialSalesLevels({});
    setConfiguredProductIds({});
    setError(null);
    setSubmitError(null);
    setLoading(true);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const cached =
          locationDetailsRef.current.id === id && locationDetailsRef.current.reloadKey === reloadKey
            ? locationDetailsRef.current
            : null;
        const [warehouseRecord, productPage, configuredMap] = await Promise.all([
          cached ? Promise.resolve(cached.warehouse) : fetchWarehouseById(id),
          loadSafetyStockProducts(page),
          cached ? Promise.resolve(cached.configured) : fetchConfiguredStockLevelMap(id).catch(() => ({})),
        ]);
        if (cancelled) return;
        locationDetailsRef.current = {
          id,
          reloadKey,
          warehouse: warehouseRecord,
          configured: configuredMap,
        };

        const productList = productPage.content || productPage.products || [];
        const elements = Number(productPage.totalElements);
        const pages = Number(productPage.totalPages);
        setWarehouse(warehouseRecord);
        setProducts(productList);
        setTotalElements(Number.isFinite(elements) ? elements : productList.length);
        setTotalPages(Number.isFinite(pages) ? pages : productList.length > 0 ? 1 : 0);
        setProductById((prev) => {
          const next = { ...prev };
          for (const product of productList) {
            if (product?.id != null && product.id !== "") next[product.id] = product;
          }
          return next;
        });

        const prefilledService = {};
        const prefilledSales = {};
        const configured = {};
        for (const product of productList) {
          const keys = [
            product.id != null ? String(product.id) : null,
            product.productId != null ? String(product.productId) : null,
          ].filter(Boolean);
          for (const key of keys) {
            const row = configuredMap[key];
            if (!row || row.configured !== true) continue;
            configured[product.id] = true;
            if (row.serviceSafetyStock != null && row.serviceSafetyStock !== "") {
              prefilledService[product.id] = row.serviceSafetyStock;
            }
            if (row.salesSafetyStock != null && row.salesSafetyStock !== "") {
              prefilledSales[product.id] = row.salesSafetyStock;
            }
            break;
          }
        }

        setServiceLevels((prev) => ({ ...prefilledService, ...prev }));
        setSalesLevels((prev) => ({ ...prefilledSales, ...prev }));
        setInitialServiceLevels((prev) => ({ ...prefilledService, ...prev }));
        setInitialSalesLevels((prev) => ({ ...prefilledSales, ...prev }));
        setConfiguredProductIds((prev) => ({ ...prev, ...configured }));
      } catch (err) {
        if (cancelled) return;
        setProducts([]);
        setError(err?.message || i18n.t("inventory.failedToLoadStockLevels"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, page, reloadKey]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const onServiceLevelChange = (productId, value) => {
    setServiceLevels((prev) => ({ ...prev, [productId]: value }));
  };

  const onSalesLevelChange = (productId, value) => {
    setSalesLevels((prev) => ({ ...prev, [productId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSaving(true);
    try {
      await persistLocationStockLevelChanges(id, {
        products: Object.values(productById).map((product) => ({
          ...product,
          serviceSafetyEnabled: isServiceSafetyEnabled(product),
          salesSafetyEnabled: isSalesSafetyEnabled(product),
        })),
        currentServiceLevels: serviceLevels,
        initialServiceLevels,
        currentSalesLevels: salesLevels,
        initialSalesLevels,
        configuredProductIds,
      });
      sessionStorage.setItem(
        TOAST_KEY,
        i18n.t("inventory.stockLevelsSavedSuccess", {
          name: warehouse?.name || "",
        })
      );
      navigate("/IMS/inventory/locations");
    } catch (err) {
      setSubmitError(err?.message || i18n.t("inventory.failedToSaveStockLevels"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  const titleRow = (
    <PageTitleRow className="mb-5" back={<PageBackLink to="/IMS/inventory/locations" />}>
      <h1 style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
        <Trans i18nKey="inventory.configureStockLevelsTitle" />
        {warehouse?.name ? (
          <span
            style={{
              fontWeight: 500,
              color: "#64748b",
              fontSize: "0.9em",
              letterSpacing: 0,
              textTransform: "none",
            }}
          >
            {warehouse.name}
          </span>
        ) : null}
      </h1>
    </PageTitleRow>
  );

  if (loading && !warehouse) {
    return (
      <AppLayout>
        <div className="inventory-module">
          {titleRow}
          <div style={{ textAlign: "center", padding: "48px 20px", color: "#64748b" }}>
            <Trans i18nKey="inventory.loadingStockLevels" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error && !warehouse) {
    return (
      <AppLayout>
        <div className="inventory-module">
          {titleRow}
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "12px",
              padding: "16px 18px",
              marginTop: "16px",
              color: "#991b1b",
              fontSize: "13px",
            }}
          >
            <p style={{ margin: "0 0 12px 0" }}>{error}</p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button type="button" className="btn-secondary" onClick={() => setReloadKey((n) => n + 1)}>
                <Trans i18nKey="attendance.retry" />
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate("/IMS/inventory/locations")}
              >
                <Trans i18nKey="inventory.backToWarehouses" />
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="inventory-module">
        {toastMessage && (
          <LeaveActionToast
            toast={{ message: toastMessage, type: "success" }}
            onClose={() => setToastMessage(null)}
          />
        )}

        <div style={{ marginBottom: "16px" }}>
          {titleRow}
        </div>

        {submitError && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "12px",
              padding: "12px 16px",
              marginBottom: "16px",
              color: "#991b1b",
              fontSize: "13px",
            }}
          >
            {submitError}
          </div>
        )}

        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "12px",
              padding: "12px 16px",
              marginBottom: "16px",
              color: "#991b1b",
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <span>{error}</span>
            <button type="button" className="btn-secondary" onClick={() => setReloadKey((n) => n + 1)}>
              <Trans i18nKey="attendance.retry" />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div
            className="table-section borderless-table-section"
            style={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
              backgroundColor: "#ffffff",
            }}
          >
            <div className="table-wrapper hidden md:block">
              <ClientTable className="leads-table">
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc" }}>
                    <th
                      style={{
                        padding: "14px 20px",
                        color: "#64748b",
                        fontSize: "11px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <Trans i18nKey="inventory.productColumn" />
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        color: "#64748b",
                        fontSize: "11px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <Trans i18nKey="inventory.partNumber" />
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        color: "#64748b",
                        fontSize: "11px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <Trans i18nKey="common.category" />
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        color: "#64748b",
                        fontSize: "11px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        textAlign: "right",
                        textTransform: "uppercase",
                        width: "180px",
                      }}
                    >
                      <Trans i18nKey="inventory.salesSafetyStockColumn" />
                    </th>
                    <th
                      style={{
                        padding: "14px 20px",
                        color: "#64748b",
                        fontSize: "11px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        textAlign: "right",
                        textTransform: "uppercase",
                        width: "180px",
                      }}
                    >
                      <Trans i18nKey="inventory.minimumStockLevelColumn" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "48px 20px", color: "#64748b" }}>
                        <Trans i18nKey="inventory.loadingStockLevels" />
                      </td>
                    </tr>
                  ) : products.length > 0 ? (
                    products.map((product) => {
                      const serviceEditable = isServiceSafetyEnabled(product);
                      const salesEditable = isSalesSafetyEnabled(product);
                      return (
                      <tr key={product.id} style={{ backgroundColor: "#ffffff" }}>
                        <td style={{ padding: "14px 20px", verticalAlign: "middle" }}>
                          <span style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>
                            {product.name}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "14px 20px",
                            color: "#64748b",
                            fontSize: "13px",
                            fontFamily: "monospace",
                          }}
                        >
                          {product.partNumber || i18n.t("inventory.emDash")}
                        </td>
                        <td style={{ padding: "14px 20px", color: "#64748b", fontSize: "13px" }}>
                          {product.category || i18n.t("inventory.emDash")}
                        </td>
                        <td style={{ padding: "14px 20px", textAlign: "right" }}>
                          <StockQtyInput
                            id={`sales-stock-${product.id}`}
                            label={i18n.t("inventory.salesSafetyStockColumn")}
                            productName={product.name}
                            value={salesLevels[product.id] ?? ""}
                            canEdit={salesEditable}
                            onChange={(value) => onSalesLevelChange(product.id, value)}
                          />
                        </td>
                        <td style={{ padding: "14px 20px", textAlign: "right" }}>
                          <StockQtyInput
                            id={`service-stock-${product.id}`}
                            label={i18n.t("inventory.minimumStockLevelColumn")}
                            productName={product.name}
                            value={serviceLevels[product.id] ?? ""}
                            canEdit={serviceEditable}
                            onChange={(value) => onServiceLevelChange(product.id, value)}
                          />
                        </td>
                      </tr>
                      );
                    })
                  ) : !error ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "48px 20px" }}>
                        <div className="flex flex-col items-center gap-3 px-5 text-center">
                          <Package size={40} style={{ color: "#94a3b8" }} />
                          <h4 style={{ margin: 0, color: "#334155", fontWeight: "600", fontSize: "15px" }}>
                            <Trans i18nKey="inventory.noProductsToConfigure" />
                          </h4>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </ClientTable>
            </div>

            <div className="md:hidden">
              {loading ? (
                <div style={{ textAlign: "center", padding: "48px 20px", color: "#64748b" }}>
                  <Trans i18nKey="inventory.loadingStockLevels" />
                </div>
              ) : products.length > 0 ? (
                <div className="divide-y divide-[var(--color-text-border)]/35">
                  {products.map((product) => (
                    <ProductStockMobileCard
                      key={product.id}
                      product={product}
                      serviceValue={serviceLevels[product.id] ?? ""}
                      salesValue={salesLevels[product.id] ?? ""}
                      onServiceChange={onServiceLevelChange}
                      onSalesChange={onSalesLevelChange}
                      serviceEditable={isServiceSafetyEnabled(product)}
                      salesEditable={isSalesSafetyEnabled(product)}
                    />
                  ))}
                </div>
              ) : !error ? (
                <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
                  <Package size={40} style={{ color: "#94a3b8" }} />
                  <h4 style={{ margin: 0, color: "#334155", fontWeight: "600", fontSize: "15px" }}>
                    <Trans i18nKey="inventory.noProductsToConfigure" />
                  </h4>
                </div>
              ) : null}
            </div>

            <TablePagination
              page={page + 1}
              totalPages={totalPages}
              totalItems={totalElements}
              pageSize={PRODUCT_PAGE_SIZE}
              disabled={loading || saving}
              onPageChange={(next) => setPage(next - 1)}
            />
          </div>

          <div
            className="form-buttons-right"
            style={{
              borderTop: "1px solid #f1f5f9",
              paddingTop: "20px",
              marginTop: "20px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate("/IMS/inventory/locations")}
              disabled={saving}
              style={{ height: "42px", padding: "0 24px" }}
            >
              <Trans i18nKey="formActions.cancel" />
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || loading || totalElements === 0}
              style={{
                height: "42px",
                padding: "0 28px",
                backgroundColor: "#152273",
                borderRadius: "6px",
              }}
            >
              {saving ? i18n.t("inventory.savingEllipsis") : i18n.t("inventory.saveStockLevels")}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
